import type { ICatalog, IOption, IPack, IProduct, IPromotion } from '@app/common';
import { CLES_PRIVEES_DE_COPIE, sansLesClesPriveesDeCopie, type ValeursDeCopie } from './cles-privees';
import { transformToUpdatableFormCatalog, type CatalogFormValues } from '../Catalog/catalog-form.helpers';
import { transformToUpdatableFormOption, type OptionFormValues } from '../Option/option-form.helpers';
import { transformToUpdatableFormPack, type PackFormValues } from '../Pack/pack-form.helpers';
import { transformToUpdatableFormProduct, type ProductFormValues } from '../Product/product-form.helpers';
import { transformToUpdatableFormPromotion, type PromotionFormValues } from '../Promotion/promotion-form.helpers';

// Réexportés : les helpers de formulaire importent depuis `cles-privees` (module feuille, sans cycle).
export { CLES_PRIVEES_DE_COPIE, sansLesClesPriveesDeCopie };
export type { ValeursDeCopie };

// Dupliquer depuis le dashboard : valeurs initiales du formulaire de création,
// calculées à partir de l'original. Chaque helper part de la transformation
// « entité → formulaire » de la modification, puis retire les identifiants
// PROPRES à l'original (racine, lignes de liaison) pour que le serveur crée de
// nouvelles lignes ; les entités PARTAGÉES (étape, variation, produit d'option,
// tag...) gardent leur `_id` : la copie les référence, elle ne les recrée pas.

export const nomDeCopie = (nom: string): string => `${nom} (copie)`;

// Retire `_id` de chaque ligne ; une relation absente donne un tableau vide.
export const sansIdentifiants = <T extends { _id?: unknown }>(lignes: T[] | undefined): Omit<T, '_id'>[] =>
  (lignes ?? []).map((ligne) => {
    const { _id, ...reste } = ligne;
    return reste;
  });

// Retire des clés d'un objet (les formulaires ont des types plus larges que
// les clés réellement présentes).
const sans = <T extends object>(objet: T, cles: string[]): T => {
  const copie = { ...objet } as Record<string, unknown>;
  cles.forEach((cle) => delete copie[cle]);
  return copie as T;
};

type AvecId = { _id?: unknown };

// Défense en profondeur : si un `populate` charge un jour les surcharges par
// restaurant (`catalogShopTo…`) sur les lignes de liaison, elles ne doivent pas
// être rattachées à la copie.
const sansSurchargesDeRestaurant = <T extends object>(ligne: T): T =>
  sans(ligne, Object.keys(ligne).filter((cle) => cle.startsWith('catalogShopTo')));

// Une ligne d'ingrédient perd son _id et son productId (ceux de l'original).
const lignesIngredientsSansIdentifiants = (lignes: AvecId[] | undefined) =>
  sansIdentifiants(lignes).map((ligne) => sans(ligne, ['productId']));

export const valeursDeCopieDuProduit = (produit: IProduct): ValeursDeCopie<ProductFormValues> => {
  const base = sans(transformToUpdatableFormProduct(produit), ['_id', 'tagsIds', 'optionsIds', 'kitchenAreasIds']);

  return {
    ...base,
    name: nomDeCopie(produit.name),
    image: null,
    catalogToProducts: [],
    // champ de formulaire recopié sur chaque déclinaison par « Appliquer les ingrédients à toutes »
    ingredients: lignesIngredientsSansIdentifiants(base.ingredients as AvecId[]),
    productVariations: sansIdentifiants(base.productVariations as AvecId[] | undefined).map((variation, index) => {
      const { productVariationToIngredients, ...reste } = variation as { productVariationToIngredients?: AvecId[] };
      return {
        ...reste,
        productVariationToIngredients: lignesIngredientsSansIdentifiants(productVariationToIngredients),
        // la transformation « entité → formulaire » ne porte pas les points fidélité
        loyaltyPointOffered: produit.productVariations?.[index]?.loyaltyPointOffered,
      };
    }) as ProductFormValues['productVariations'],
    copieDe: produit._id,
    copieDesImagesDe: produit._id,
    imagesDeLOriginal: produit.images,
  };
};

export const valeursDeCopieDuMenu = (menu: IPack): ValeursDeCopie<PackFormValues> => {
  const base = sans(transformToUpdatableFormPack(menu), ['_id']);

  return {
    ...base,
    name: nomDeCopie(menu.name),
    image: null,
    // Seulement si le menu en a : `null` partirait dans le FormData comme
    // chaîne « null », que l'engine convertit en NaN et refuse.
    ...(typeof menu.loyaltyPointOffered === 'number' ? { loyaltyPointOffered: menu.loyaltyPointOffered } : {}),
    catalogToPacks: [],
    packToSteps: sansIdentifiants(base.packToSteps as AvecId[] | undefined) as PackFormValues['packToSteps'],
    packToProductVariations: sansIdentifiants(
      base.packToProductVariations as AvecId[] | undefined,
    ) as PackFormValues['packToProductVariations'],
    copieDe: menu._id,
    copieDesImagesDe: menu._id,
    imagesDeLOriginal: menu.images,
  };
};

export const valeursDeCopieDeLOption = (option: IOption): ValeursDeCopie<OptionFormValues> => ({
  ...sans(transformToUpdatableFormOption(option), ['_id', 'productsIds']),
  name: nomDeCopie(option.name),
  copieDe: option._id,
});

export const valeursDeCopieDeLaPromotion = (promotion: IPromotion): ValeursDeCopie<PromotionFormValues> => {
  const base = sans(transformToUpdatableFormPromotion(promotion), [
    '_id',
    'usedCount',
    'usedCustomersIds',
    'usages',
    'catalogToPromotions',
  ]);

  return {
    ...base,
    name: nomDeCopie(promotion.name),
    image: null,
    code: '',
    conditions: sansIdentifiants(base.conditions as AvecId[] | undefined) as PromotionFormValues['conditions'],
    actions: sansIdentifiants(base.actions as AvecId[] | undefined) as PromotionFormValues['actions'],
    copieDe: promotion._id,
    copieDesImagesDe: promotion._id,
    imagesDeLOriginal: promotion.images,
  };
};

export const valeursDeCopieDuCatalogue = (catalogue: ICatalog): ValeursDeCopie<CatalogFormValues> => {
  const base = sans(transformToUpdatableFormCatalog(catalogue, []), ['_id']);

  return {
    ...base,
    name: nomDeCopie(catalogue.name),
    catalogToShops: [],
    catalogToConnectors: [],
    catalogToProducts: sansIdentifiants(base.catalogToProducts as AvecId[] | undefined).map((ligne) => {
      const { catalogProductToProductVariations, ...reste } = sansSurchargesDeRestaurant(ligne) as {
        catalogProductToProductVariations?: AvecId[];
      };
      return { ...reste, catalogProductToProductVariations: sansIdentifiants(catalogProductToProductVariations) };
    }) as CatalogFormValues['catalogToProducts'],
    catalogToPacks: sansIdentifiants(base.catalogToPacks as AvecId[] | undefined).map(
      sansSurchargesDeRestaurant,
    ) as CatalogFormValues['catalogToPacks'],
    catalogToPromotions: sansIdentifiants(base.catalogToPromotions as AvecId[] | undefined).map(
      sansSurchargesDeRestaurant,
    ) as CatalogFormValues['catalogToPromotions'],
    catalogToTags: sansIdentifiants(base.catalogToTags as AvecId[] | undefined) as CatalogFormValues['catalogToTags'],
    catalogToExcludedOptions: sansIdentifiants(
      base.catalogToExcludedOptions as AvecId[] | undefined,
    ) as CatalogFormValues['catalogToExcludedOptions'],
    catalogToExcludedSteps: sansIdentifiants(
      base.catalogToExcludedSteps as AvecId[] | undefined,
    ) as CatalogFormValues['catalogToExcludedSteps'],
    copieDe: catalogue._id,
  };
};
